import type { ReactNode } from 'react';
import type { BusinessType, Lang } from '@app/shared';
import { initialsOf } from '../domain/people';
import { useI18n } from '../i18n/I18nProvider';
import { ICON_STROKE, INDUSTRY_ICON, UserRound } from './icons';

/** Фильтр-«таблетка» каталога (`.chip` прототипа). */
export function Chip(props: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-2 text-[12.5px] font-semibold transition ${
        props.active ? 'bg-accent text-on-accent' : 'bg-surface-2 text-ink-2'
      }`}
    >
      {props.children}
    </button>
  );
}

/** Бейдж статуса записи (`.tag` прототипа). */
export function Tag(props: { tone: 'new' | 'ok' | 'done' | 'cancelled' | 'noshow'; children: ReactNode }): JSX.Element {
  const look = {
    new: 'bg-[color-mix(in_srgb,var(--gold)_20%,transparent)] text-gold',
    ok: 'bg-[color-mix(in_srgb,var(--accent)_18%,transparent)] text-accent',
    done: 'bg-[color-mix(in_srgb,var(--green)_20%,transparent)] text-green',
    cancelled: 'bg-surface-2 text-ink-3',
    noshow: 'bg-[color-mix(in_srgb,var(--red)_20%,transparent)] text-danger',
  }[props.tone];

  return (
    <span
      className={`whitespace-nowrap rounded-[7px] px-2 py-[3px] text-[9.5px] font-extrabold uppercase tracking-[0.04em] ${look}`}
    >
      {props.children}
    </span>
  );
}

/**
 * Плитка с иконкой отрасли.
 *
 * Принимает ОТРАСЛЬ, а не готовую иконку: иначе выбор картинки размазался бы по пяти
 * экранам и разъехался бы при первой же правке — см. карту `INDUSTRY_ICON`.
 */
export function IndustryTile(props: {
  type: BusinessType;
  size?: 'sm' | 'md' | 'lg';
}): JSX.Element {
  const size = props.size ?? 'md';
  const { box, icon } = {
    sm: { box: 'h-[42px] w-[42px] rounded-[12px]', icon: 20 },
    md: { box: 'h-[52px] w-[52px] rounded-[15px]', icon: 24 },
    lg: { box: 'h-[70px] w-[70px] rounded-[20px]', icon: 32 },
  }[size];
  const Icon = INDUSTRY_ICON[props.type];

  return (
    <div
      className={`flex shrink-0 items-center justify-center bg-surface-2 text-ink ${box}`}
      aria-hidden="true"
    >
      <Icon size={icon} strokeWidth={ICON_STROKE} />
    </div>
  );
}

/**
 * Аватар человека или заведения.
 *
 * Круглый — в отличие от квадратной плитки отрасли: так человек и организация различимы
 * с одного взгляда, не читая подписи.
 *
 * Фотографии пока нет ни у мастеров, ни у заведений (колонки в схеме нет), поэтому
 * рисуются инициалы — см. `initialsOf`. Место под снимок физически занято: когда поле
 * появится, сюда добавится `<img>`, а вёрстка вокруг не шелохнётся.
 */
export function Avatar(props: { name: string; size?: 'sm' | 'md' | 'lg' }): JSX.Element {
  const { box, text, icon } = {
    sm: { box: 'h-[42px] w-[42px]', text: 'text-[14px]', icon: 18 },
    md: { box: 'h-[52px] w-[52px]', text: 'text-[17px]', icon: 22 },
    lg: { box: 'h-[70px] w-[70px]', text: 'text-[23px]', icon: 30 },
  }[props.size ?? 'md'];
  const initials = initialsOf(props.name);

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-surface-2 font-bold text-ink-2 ${box} ${text}`}
      aria-hidden="true"
    >
      {initials === '' ? <UserRound size={icon} strokeWidth={ICON_STROKE} /> : initials}
    </div>
  );
}

/**
 * Переключатель RU/UZ (ТЗ §14.3).
 *
 * Живёт в шапке экрана, а не в отдельных «настройках»: в приложении, где нет ни регистрации,
 * ни профиля, спрятанный переключатель языка эквивалентен его отсутствию.
 */
export function LangSwitch(): JSX.Element {
  const { lang, setLang } = useI18n();
  const options: readonly Lang[] = ['ru', 'uz'];

  return (
    <div className="flex shrink-0 rounded-[10px] bg-surface-2 p-[3px]">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => setLang(option)}
          aria-pressed={lang === option}
          className={`rounded-[7px] px-2.5 py-1.5 text-[11px] font-bold uppercase transition ${
            lang === option ? 'bg-accent text-on-accent' : 'text-ink-3'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );
}
